import moment from "moment";

// utils/formatNumber.js
export function formatNumberShort(value) {
  if (value === null || value === undefined) return "0";

  const absValue = Math.abs(value);
  let number,
    suffix = "";

  if (absValue >= 1_000_000_000) {
    number = value / 1_000_000_000;
    suffix = "B";
  } else if (absValue >= 1_000_000) {
    number = value / 1_000_000;
    suffix = "M";
  } else if (absValue >= 1_000) {
    number = value / 1_000;
    suffix = "k";
  } else {
    number = value;
  }

  // 👇 floor instead of round
  // number = Math.floor(number * 10) / 10;
  number = Math.floor(number * 100) / 100;

  return `${number % 1 === 0 ? number : number.toFixed(2)}${suffix}`;
}

export const formatNumber = (value) =>
  value ? Number(value).toLocaleString("en-US") : "";

export const removeCommas = (value) => value.replace(/,/g, "");

export const formatCurrency = (value, currency = "USD") => {
  if (value === null || value === undefined || isNaN(value)) return "$0.00";

  return Number(value).toLocaleString("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatDate = (date, format = "MM-DD-YYYY") => {
  if (!date) return "-";
  return moment(date).format(format);
};

// utils/statusColors.js
export const getStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "bg-emerald-500"; // bright green

    case "Under Contract":
      return "bg-sky-500"; // light blue

    case "Pending":
      return "bg-yellow-400"; // bright yellow

    case "Closed":
      return "bg-slate-500"; // neutral gray

    case "Cancelled":
      return "bg-rose-500"; // strong red/pink

    case "Expired":
      return "bg-orange-500"; // orange

    case "Prospect":
      return "bg-violet-500"; // purple

    case "On Hold":
      return "bg-amber-600"; // dark amber (different from yellow)

    case "CMAs":
      return "bg-indigo-600"; // deep indigo (different from blue)

    case "Listing Appointment":
      return "bg-fuchsia-500"; // pink-purple

    case "Pre-Market":
      return "bg-lime-500"; // lime green (different from emerald)

    case "Coming Soon":
      return "bg-cyan-500"; // cyan (different from sky)

    default:
      return "bg-gray-400";
  }
};

export const formatText = (value) => {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const newmessagesuccess = async (
  title,
  id,
  message,
  taskid,
  type,
  dealId,
) => {
  try {
    // const res = await fetch('https://us-central1-raftekpro-82bb0.cloudfunctions.net/api/newslot', {
    const res = await fetch(
      // "https://team-edelstein.web.app/newmessage",
      "https://us-central1-team-edelstein.cloudfunctions.net/api/newmessage",

      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: title,
          message: message,
          id: id,
          taskid: taskid,
          dealid: dealId,
          type: type,
        }),
      },
    );

    if (res.status === 200) {
      console.log("new message sent " + JSON.stringify(res));
      const data = await res.json();

      console.log("Successfully Notified User");
    } else {
      console.log("Notififcation Error" + JSON.stringify(res));
    }
  } catch (error) {
    console.error("newmessagesuccess error:", error);
  }
};

export const formatDates = (date) => {
  if (!date || date === "") return "";

  const [yyyy, mm, dd] = date?.split("-");
  return `${mm}/${dd}/${yyyy}`;
}

// ============ DEAL APPROVAL NOTIFICATIONS ============
// User type values: 0 active, 1 deleted, 2 disabled.

export function selectApprovers(users) {
  return (users || []).filter(
    (u) =>
      u?.type !== 1 &&
      u?.type !== 2 &&
      (u?.role === "Admin" || u?.role === "Supervisor"),
  );
}

// Client Care = Supervisor role. File requests are handled by Client Care only.
export function selectClientCare(users) {
  return (users || []).filter(
    (u) => u?.type !== 1 && u?.type !== 2 && u?.role === "Supervisor",
  );
}

export function selectAccountants(users) {
  return (users || []).filter(
    (u) => u?.type !== 1 && u?.type !== 2 && u?.role === "Accountant",
  );
}

// Stage 1: notify the right audience when a pending deal is submitted.
// Recipients and title vary by requestType:
//   - "saleTurnIn" → Admins + Client Care (Supervisors) review and approve
//   - file request (default) → Client Care only handles the file
// Safe to call even if there are no recipients (no-op).
export async function notifyApprovers(deal, allUsers, submitter) {
  const isSaleTurnIn = deal?.requestType === "saleTurnIn";
  const recipients = isSaleTurnIn
    ? selectApprovers(allUsers)
    : selectClientCare(allUsers);
  if (recipients.length === 0) return;
  const submitterName =
    `${submitter?.firstname || ""} ${submitter?.lastname || ""}`.trim() ||
    "An agent";
  const subject =
    deal?.propertyaddress ||
    (deal?.dealtype ? `${deal.dealtype} request` : "New deal");
  const message = `${submitterName} · ${subject}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  const title = isSaleTurnIn
    ? "New deal pending approval"
    : "New listing — file to be created";
  const notifType = isSaleTurnIn ? "pending-approval" : "pending-file";
  await Promise.all(
    recipients.map((u) =>
      newmessagesuccess(title, u.id, message, dealRef, notifType, dealRef),
    ),
  );
}

// Notify the requesting agent when their file request has been completed by Client Care.
// Safe to call even if the recipient lookup fails (no-op).
export async function notifyAgentOnFileComplete(deal, allUsers, completer) {
  const recipientId = deal?.createdby;
  if (!recipientId) return;
  const recipient = (allUsers || []).find((u) => u?.id === recipientId);
  if (!recipient || recipient.type === 1 || recipient.type === 2) return;
  const completerName =
    `${completer?.firstname || ""} ${completer?.lastname || ""}`.trim() ||
    "Client Care";
  const subject = deal?.propertyaddress || "Your file";
  const message = `${subject} · completed by ${completerName}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  await newmessagesuccess(
    "Your file is ready",
    recipientId,
    message,
    dealRef,
    "file-completed",
    dealRef,
  );
}

// Email the agent listed on a file (deal.agentData) when Client Care marks it
// complete. Additive to notifyAgentOnFileComplete, which sends an in-app
// notification to the submitter (deal.createdby). The Cloud Function sends the
// email AS the completer, so the completer must have a connected Gmail token.
//
// Returns a result the caller uses to pick a toast:
//   { ok: true, gmailMessageId? }
//   { ok: false, reason: "no-recipient" }       // no agentData / not found / no email
//   { ok: false, reason: "no-gmail" }            // completer has no gmail token
//   { ok: false, reason: "send-failed", error }  // endpoint returned an error
export async function emailAgentOnFileComplete(deal, allUsers, completer) {
  const agentId = deal?.agentData;
  if (!agentId) return { ok: false, reason: "no-recipient" };

  const agent = (allUsers || []).find((u) => u?.id === agentId);
  if (!agent || !agent.email) return { ok: false, reason: "no-recipient" };

  if (!completer?.gmailtoken?.access_token) {
    return { ok: false, reason: "no-gmail" };
  }

  const { sendOpenHouseEmail } = await import("./emailSender");

  const agentFirst = agent.firstname || "there";
  const agentFullName =
    `${agent.firstname || ""} ${agent.lastname || ""}`.trim() || "Agent";
  const completerName =
    `${completer?.firstname || ""} ${completer?.lastname || ""}`.trim() ||
    "Client Care";
  const address = deal?.propertyaddress || "";
  const subject = address
    ? `File Complete — ${address}`
    : "Your File Is Complete";
  const propertyPhrase = address ? `your file for ${address}` : "your file";

  const text = [
    `Hi ${agentFirst},`,
    ``,
    `Good news — ${propertyPhrase} has been completed by ${completerName}.`,
    `No action is needed on your end.`,
    ``,
    `— Team Edelstein`,
  ].join("\n");

  const html =
    `<p>Hi ${agentFirst},</p>` +
    `<p>Good news — ${propertyPhrase} has been completed by ${completerName}.</p>` +
    `<p>No action is needed on your end.</p>` +
    `<p>— Team Edelstein</p>`;

  const result = await sendOpenHouseEmail({
    userId: completer.id,
    to: agent.email,
    toName: agentFullName,
    subject,
    text,
    html,
    type: "file-completed",
    relatedDealId: String(deal?.createdat || deal?.id || ""),
  });

  if (result?.ok) return { ok: true, gmailMessageId: result.gmailMessageId };
  return { ok: false, reason: "send-failed", error: result?.error };
}

// Email the configured "listing/buyer request" recipients (set in Settings →
// Notifications) when a new request is submitted. Independent of and
// additional to the in-app notifyApprovers flow.
export async function emailListingRequestRecipients(deal, submitter) {
  // Lazy imports to avoid a circular dependency (helper.js is widely imported).
  const { loadListingRequestRecipients } = await import(
    "./notificationSettings"
  );
  const { sendOpenHouseEmail } = await import("./emailSender");
  const { getAllusers } = await import("./firebasefunctions");

  try {
    const lists = await loadListingRequestRecipients();
    const isBuyer = deal?.dealtype === "Buyer";
    const userIds = isBuyer ? lists.buyer : lists.seller;
    if (!userIds || userIds.length === 0) return;

    const allUsers = await getAllusers("users");
    const recipients = (allUsers || []).filter(
      (u) =>
        userIds.includes(u.id) &&
        u.type !== 1 &&
        u.type !== 2 &&
        u.email,
    );
    if (recipients.length === 0) return;

    const submitterName =
      `${submitter?.firstname || ""} ${submitter?.lastname || ""}`.trim() ||
      "An agent";
    const agent = (allUsers || []).find((u) => u?.id === deal?.agentData);
    const agentName = agent
      ? `${agent.firstname || ""} ${agent.lastname || ""}`.trim()
      : "";
    const address = deal?.propertyaddress || "(no address provided)";
    const requestKind = isBuyer ? "Buyer Request" : "Listing Request";
    const subject = `New ${requestKind} — ${address}`;

    // Who referred the lead — only meaningful when a client referral was
    // marked "yes" on the request form.
    const referredBy =
      deal?.clientReferal === "yes" && deal?.referredSendGift
        ? deal.referredSendGift
        : null;

    const lines = [
      `${submitterName} just submitted a new ${requestKind}.`,
      ``,
      agentName ? `Agent: ${agentName}` : null,
      `Property: ${address}`,
      deal?.totalcommissionrate
        ? `Commission: ${deal.totalcommissionrate}%`
        : null,
      deal?.listingDate
        ? `Listing appointment: ${formatDate(deal.listingDate)}`
        : null,
      deal?.fileDeadline
        ? `Need file by: ${formatDate(deal.fileDeadline)}`
        : null,
      referredBy ? `Referred by: ${referredBy}` : null,
      deal?.notes ? `Notes: ${deal.notes}` : null,
      ``,
      `Open in the CRM to start the file.`,
    ].filter(Boolean);
    const body = lines.join("\n");

    const htmlLines = [
      `<p>${submitterName} just submitted a new ${requestKind}.</p>`,
      `<ul>`,
      agentName ? `<li><strong>Agent:</strong> ${agentName}</li>` : null,
      `<li><strong>Property:</strong> ${address}</li>`,
      deal?.totalcommissionrate
        ? `<li><strong>Commission:</strong> ${deal.totalcommissionrate}%</li>`
        : null,
      deal?.listingDate
        ? `<li><strong>Listing appointment:</strong> ${formatDate(deal.listingDate)}</li>`
        : null,
      deal?.fileDeadline
        ? `<li><strong>Need file by:</strong> ${formatDate(deal.fileDeadline)}</li>`
        : null,
      referredBy
        ? `<li><strong>Referred by:</strong> ${referredBy}</li>`
        : null,
      deal?.notes ? `<li><strong>Notes:</strong> ${deal.notes}</li>` : null,
      `</ul>`,
      `<p>Open in the CRM to start the file.</p>`,
    ].filter(Boolean);
    const html = htmlLines.join("");

    await Promise.all(
      recipients.map((r) =>
        sendOpenHouseEmail({
          userId: submitter?.id,
          to: r.email,
          toName: `${r.firstname || ""} ${r.lastname || ""}`.trim(),
          subject,
          text: body,
          html,
          type: "listing-request-notification",
          relatedDealId: deal?.createdat || deal?.id || "",
        }).catch((err) =>
          console.error("Listing request email failed:", r.email, err),
        ),
      ),
    );
  } catch (err) {
    console.error("emailListingRequestRecipients failed:", err);
  }
}

// Stage 2: notify Accountants when a deal transitions to Approved.
// Caller must guard with the prior-status check (see plan tasks 8 and 9).
export async function notifyAccountantsOnApproval(deal, allUsers, approver) {
  const recipients = selectAccountants(allUsers);
  if (recipients.length === 0) return;
  const approverName =
    `${approver?.firstname || ""} ${approver?.lastname || ""}`.trim() ||
    "An approver";
  const subject = deal?.propertyaddress || "Deal";
  const message = `${subject} · approved by ${approverName}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  await Promise.all(
    recipients.map((u) =>
      newmessagesuccess(
        "Deal ready for accounting review",
        u.id,
        message,
        dealRef,
        "approved-for-accounting",
        dealRef,
      ),
    ),
  );
}
